/**
 * RGM Starter Studio · Mixer Side Drawer (Phase 1B-live).
 *
 * Slide-in RIGHT panel (was a bottom sheet). Frees the vertical
 * recording workspace on mobile while keeping the transport and
 * timeline reachable. Opens/closes only via the 🎚️ transport
 * button — lane tap merely selects the channel.
 *
 * Controls preserved: Volume · Pan (with L/C/R indicator) · Mute ·
 * Solo · Monitor. Solo semantics remain standard — isolate the
 * selected channel during playback (no audio-quality inference).
 */
import React from "react";
import { laneColorTokens, LANE_PALETTE } from "./WaveformLane";

const ROLE_LABEL = { lead: "Lead", double: "Double", adlib: "Ad-lib", bg: "Background", custom: "Vocal" };

export default function MixerDrawer({
  track,
  index,
  open,
  onClose,
  onChange,
}) {
  if (!open) return null;

  const tokens = laneColorTokens(track?.color);
  const patch = (partial) => track && onChange?.(track.track_id, partial);
  const pan = track?.pan || 0;
  const panLabel = pan === 0 ? "C" : (pan < 0 ? `L ${Math.round(Math.abs(pan) * 100)}` : `R ${Math.round(pan * 100)}`);

  return (
    <>
      <div
        className="fixed inset-0 z-30 bg-black/40"
        onClick={onClose}
        data-testid="mixer-scrim"
      />
      <aside
        className="fixed top-0 bottom-[76px] right-0 z-40 w-72 max-w-[85vw] bg-[#0d0a18] border-l border-white/10 shadow-[-8px_0_24px_rgba(0,0,0,0.55)] overflow-y-auto"
        data-testid="studio-mixer-drawer"
        role="dialog"
        aria-label="Mixer"
      >
        <header className="flex items-center gap-2 px-4 h-12 border-b border-white/10">
          <span className={`inline-block w-3 h-3 rounded-full ${tokens.swatch}`} />
          <p className="text-xs font-bold text-white/90 flex-1 truncate">
            {track ? `Voice ${index + 1} · ${ROLE_LABEL[track.role] || track.role}` : "No lane selected"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white text-xs"
            data-testid="mixer-close-btn"
            aria-label="Close mixer"
          >Close ✕</button>
        </header>

        {!track && (
          <div className="p-4 text-xs text-gray-500" data-testid="mixer-no-lane">
            Tap a vocal lane on the timeline to route its channel here.
          </div>
        )}

        {track && (
          <div className="p-4 space-y-4">
            {/* Name + lane colour */}
            <div>
              <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Track name</span>
              <input
                key={track.track_id}
                defaultValue={track.label || "Vocal"}
                maxLength={40}
                onBlur={(e) => {
                  const v = e.target.value.trim().slice(0, 40);
                  if (v && v !== track.label) patch({ label: v });
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="mt-1.5 w-full h-9 px-2 rounded bg-black/40 border border-white/10 text-sm text-white focus:outline-none focus:border-fuchsia-400/60"
                data-testid="mixer-name-input"
              />
              <div className="flex gap-2 mt-2" role="radiogroup" aria-label="Lane colour">
                {Object.keys(LANE_PALETTE).map((c) => (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={track.color === c}
                    aria-label={c}
                    onClick={() => patch({ color: c })}
                    className={`w-7 h-7 rounded-full ${LANE_PALETTE[c].swatch} ${track.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0d0a18]" : "opacity-70"}`}
                    data-testid={`mixer-color-${c}`}
                  />
                ))}
              </div>
            </div>

            {/* Volume */}
            <label className="block">
              <span className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                <span>Volume</span>
                <span className="tabular-nums text-gray-500" data-testid="mixer-gain-value">
                  {(track.gain_db || 0).toFixed(1)} dB
                </span>
              </span>
              <input
                type="range" min={-24} max={6} step={0.5}
                value={track.gain_db || 0}
                onChange={(e) => patch({ gain_db: parseFloat(e.target.value) })}
                className="w-full accent-fuchsia-500 mt-2"
                data-testid="mixer-gain-slider"
              />
            </label>

            {/* Pan */}
            <div>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                <span>Pan</span>
                <span className="tabular-nums" data-testid="mixer-pan-value">
                  <PanIndicator pan={pan} />
                </span>
              </div>
              <input
                type="range" min={-1} max={1} step={0.05}
                value={pan}
                onChange={(e) => patch({ pan: parseFloat(e.target.value) })}
                className="w-full accent-fuchsia-500 mt-2"
                data-testid="mixer-pan-slider"
                aria-label={`Pan ${panLabel}`}
              />
              <div className="flex justify-between text-[9px] text-gray-500 mt-1 tabular-nums">
                <span>L</span><span>C</span><span>R</span>
              </div>
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-2 gap-2">
              <ToggleButton
                testid="mixer-mute-btn"
                active={!!track.mute}
                onClick={() => patch({ mute: !track.mute })}
                label="Mute"
                activeClass="bg-red-600/80 border-red-400 text-white"
              />
              <ToggleButton
                testid="mixer-solo-btn"
                active={!!track.solo}
                onClick={() => patch({ solo: !track.solo })}
                label="Solo"
                activeClass="bg-amber-500/80 border-amber-300 text-black"
                title="Isolate this lane during playback"
              />
            </div>
            <p className="text-[10px] text-gray-500 -mt-1" data-testid="mixer-solo-hint">
              Solo isolates this lane during playback. Mic monitoring is one global switch in the Studio tools bar.
            </p>
          </div>
        )}
      </aside>
    </>
  );
}

function PanIndicator({ pan }) {
  const val = pan || 0;
  const side = val === 0 ? "C" : (val < 0 ? "L" : "R");
  const amount = val === 0 ? "" : ` ${Math.round(Math.abs(val) * 100)}`;
  return (
    <span className="inline-flex items-center gap-1 text-gray-400">
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${val < 0 ? "bg-fuchsia-500/70 text-white" : "bg-white/5 text-gray-500"}`}>L</span>
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${val === 0 ? "bg-fuchsia-500/70 text-white" : "bg-white/5 text-gray-500"}`}>C</span>
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${val > 0 ? "bg-fuchsia-500/70 text-white" : "bg-white/5 text-gray-500"}`}>R</span>
      <span className="ml-1 text-gray-500 text-[10px] tabular-nums">{side}{amount}</span>
    </span>
  );
}

function ToggleButton({ testid, active, onClick, label, activeClass, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      title={title}
      className={`h-9 rounded border text-[11px] font-bold transition-colors ${
        active ? activeClass : "border-white/15 text-gray-300 hover:border-white/30"
      }`}
    >
      {label}
    </button>
  );
}
